/* eslint-disable max-lines */
//
//  StringUtils.ts
//  Pulsar Language
//
//  Created by Jiri Trecak.
//  Copyright © 2020 Supernova. All rights reserved.
//
export class StringUtils {
    /** Returns string without whitespaces and newlines, trimmed left and right */
    static trimmed(str) {
        return str.trim();
    }
    /** Returns string without double quotes, trimmed left and right */
    static withoutDoubleQuotes(str) {
        return str.replace(/(^"|"$)/g, "");
    }
    /** Returns string without single and double quotes, trimmed left and right */
    static withoutQuotes(str) {
        return str.replace(/(^'|'$)/g, "");
    }
    /** Returns opening string until the first occurance of the character. If delimiter is not found, returns null */
    static substringUntilDelimiter(str, delimiter) {
        let buffer = "";
        for (const c of str) {
            if (c !== delimiter) {
                buffer += c;
            }
            else {
                return buffer;
            }
        }
        return null;
    }
    /** Returns whether string is prefixed and suffixed with double quotes */
    static isSurroundedWithDoubleQuotes(str) {
        return str.startsWith('"') && str.endsWith('"');
    }
    /** Returns substring between two round brackets. Returns null if it doesn't fit the format */
    static argumentStringBetweenBrackets(str, keyword) {
        let argument = str;
        if (argument.startsWith(keyword)) {
            argument = argument.substr(keyword.length);
        }
        else {
            // TODO: Rewrite
            // throw NSError(domain: "", code: 0, userInfo: [NSLocalizedDescriptionKey: "String is not valid for keyword \(keyword)"])
            return null;
        }
        if (argument.startsWith("(") && argument.endsWith(")")) {
            argument = argument.substr(1, argument.length - 2);
        }
        else {
            // TODO: Rewrite
            // throw NSError(domain: "", code: 0, userInfo: [NSLocalizedDescriptionKey: "Argument must be prefixed with ( and suffixed with )"])
            return null;
        }
        return argument;
    }
    /** Returns substring between two round brackets using syntax-sensitive parsing (ignoring quoted content and properly balancing number of ( ) */
    static argumentStringBetweenBracketsFirstOccurance(str, keyword) {
        // This recieves keyword(...)...
        let argument = str;
        if (argument.startsWith(keyword)) {
            argument = argument.substr(keyword.length);
        }
        else {
            // TODO: Rewrite
            // throw NSError(domain: "", code: 0, userInfo: [NSLocalizedDescriptionKey: "String is not valid for keyword \(keyword)"])
            return null;
        }
        // Test if argument starts with (
        if (argument.startsWith("(")) {
            argument = argument.substr(1);
        }
        else {
            // TODO: Rewrite
            // throw NSError(domain: "", code: 0, userInfo: [NSLocalizedDescriptionKey: "Argument must be prefixed with ("])
            return null;
        }
        // At this point, we have ...)... and we start parsing with openedBrackets count at 1
        let buffer = "";
        let lock = null;
        let previousChar = null;
        let openedBrackets = 1;
        for (const c of argument) {
            // Iterate each character, testing for locks
            // Last character escapes so ignore next one
            if (previousChar !== "\\") {
                if (lock === c) {
                    // Lock pair found, unlock
                    lock = null;
                }
                else if (lock === null && (c === "'" || c === '"')) {
                    // Lock character found, but if it was escaped, ignore it
                    lock = c;
                }
            }
            // If content is not locked and character is either opening or closing bracket, we increase/decrease the level
            if (lock === null && c === "(") {
                openedBrackets += 1;
            }
            else if (lock === null && c === ")") {
                if (openedBrackets > 0) {
                    openedBrackets -= 1;
                }
                // If we have closed the opening bracket, return the argument content
                if (openedBrackets === 0) {
                    return buffer;
                }
            }
            previousChar = c;
            buffer += c;
        }
        // TODO: Rewrite
        // throw NSError(domain: "", code: 0, userInfo: [NSLocalizedDescriptionKey: "Argument must be suffixed with )"])
        return null;
    }
    /** Returns string without single and double quotes but removes only 1 character at most */
    static withoutQuotesSingleOccurance(str) {
        let string = str;
        if (string.startsWith('"') || string.startsWith("'")) {
            string = string.substr(1);
        }
        if (string.endsWith('"') || string.endsWith("'")) {
            string = string.substr(0, string.length - 1);
        }
        return string;
    }
    /** Returns string without single and double quotes but removes only 1 character at most */
    static withoutDoubleQuotesSingleOccurance(str) {
        let string = str;
        if (string.startsWith('"')) {
            string = string.substr(1);
        }
        if (string.endsWith('"')) {
            string = string.substr(0, string.length - 1);
        }
        string = string.replace(/\\"/g, '"'); // unmask quotes
        return string;
    }
    /** Returns string without round brackets, trimmed left and right */
    static withoutRoundBrackets(str) {
        return str.replace("^[(]+|[)]+$", "");
    }
    /** Creates a full range of a string, returning range of (0, length) */
    static fullRange(str) {
        return { location: 0, length: str.length };
    }
    /** Creates string made of N spaces */
    static spaces(count) {
        return " ".repeat(count);
    }
    /** Splits string using separator while properly handling quotes.
    Content inside single or double quote will be ignored. Only content within closed quote pair will be ignored,
    Last single, not closed quote will be ignored. Quote types can't be combined, if the quote pair
    started with one type, it needs to be closed with the same one. There can be multiple types of quote pairs in single string though.
    Returns original string without splitting if separator is single or double quote as this behavior is undefined */
    static splitIgnoringQuotedContent(str, separator) {
        if (separator === '"' || separator === "'") {
            return [str];
        }
        let components = [];
        let buffer = "";
        let lock = null;
        let previousChar = null;
        let lastQuotePairPosition = null;
        for (const [index, c] of str.split("").entries()) {
            // Iterate each character, testing for locks
            if (c === separator) {
                // Found separator, ignore the split if lock is active, otherwise add word to buffer
                if (lock === null) {
                    components.push(buffer);
                    buffer = "";
                    lastQuotePairPosition = null;
                }
                else {
                    buffer += c;
                }
            }
            else {
                // Separator not found
                buffer += c;
                // Last character escapes so ignore next one
                if (previousChar !== "\\") {
                    if (lock === c) {
                        // Lock pair found, unlock
                        lock = null;
                        lastQuotePairPosition = index;
                    }
                    else if (lock === null && (c === "'" || c === '"')) {
                        // Lock character found, but if it was escaped, ignored it
                        lock = c;
                    }
                }
                // Remove lock assuming last character wasn't escaped
            }
            previousChar = c;
        }
        // If there is some word, append it
        if (buffer.length > 0) {
            // Note that if the lock was opened but not closed, there is potentially last piece that wasn't split and should be
            // This is an edge case so we are treating it as such.
            // Could be optimized further just by saving whether separator was found inside lock but no need
            if (lock !== null) {
                // Gracefully handle edge case situation where following: "test \"test test\"\" test" can happen
                // Another quote is opened after some content with separator inside was ignored, meaning that separation of last buffer will actually result
                // in incorrectly separating content within quotes. So we split only the part after the last quote and prepend the prefix
                if (lastQuotePairPosition) {
                    const substring = str.substr(lastQuotePairPosition, str.length - lastQuotePairPosition);
                    const subcomponents = substring.split(separator);
                    // Prepend the string with the unsplit part
                    if (subcomponents.length > 0) {
                        let firstItem = subcomponents[0];
                        firstItem = `${buffer.substring(0, buffer.length - substring.length)}${firstItem}`;
                        subcomponents.splice(0, 1, firstItem);
                    }
                    // Append separated components
                    components = components.concat(subcomponents);
                }
                else {
                    components = components.concat(buffer.split(separator));
                }
            }
            else {
                components.push(buffer);
            }
        }
        return components;
    }
    /** Removes spaces when they are inside of brackets, but properly handling spaces inside quoted content which should remain intact */
    static withoutSpacesInsideRoundBrackets(str) {
        let bracketCount = 0;
        let isInsideQuotes = false;
        let previousChar = null;
        let result = "";
        for (const char of str) {
            switch (char) {
                case "(":
                    if (!isInsideQuotes) {
                        bracketCount += 1;
                    }
                    break;
                case ")":
                    if (!isInsideQuotes) {
                        bracketCount -= 1;
                    }
                    break;
                case '"':
                    if (previousChar && previousChar === "\\") {
                        // Escaped quotes do not change "isInsideQuotes"
                    }
                    else {
                        isInsideQuotes = !isInsideQuotes;
                    }
                    break;
                default:
                    break;
            }
            if (char === " ") {
                if (bracketCount === 0 || isInsideQuotes) {
                    result += char;
                }
            }
            else {
                result += char;
            }
            previousChar = char;
        }
        return result;
    }
    /** Splits string using separator while properly handling quotes and ignoring all content that comes in ().
    Content inside single or double quote will be ignored. Only content within closed quote pair will be ignored,
    Last single, not closed quote will be ignored. Quote types can't be combined, if the quote pair
    started with one type, it needs to be closed with the same one. There can be multiple types
    of quote pairs in single string though.
    Returns original string without splitting if separator is single, double quote or left/right bracket as this behavior is undefined */
    static splitIgnoringQuotedBracketedContent(str, separator) {
        if (separator === '"' ||
            separator === "'" ||
            separator === "(" ||
            separator === ")" ||
            separator === "[" ||
            separator === "]" ||
            separator === "{" ||
            separator === "}") {
            return [str];
        }
        let components = [];
        let buffer = "";
        let lock = null;
        let previousChar = null;
        let lastQuotePairPosition = null;
        let openedRoundBrackets = 0;
        let openedSquareBrackets = 0;
        let openedCurlyBrackets = 0;
        for (const [index, c] of str.split("").entries()) {
            // Iterate each character, testing for locks
            if (c === separator) {
                // Found separator, ignore the split if lock is active, or ignore the split if inside bracket, otherwise add word to buffer
                if (lock === null &&
                    openedSquareBrackets === 0 &&
                    openedRoundBrackets === 0 &&
                    openedCurlyBrackets === 0) {
                    components.push(buffer);
                    buffer = "";
                    lastQuotePairPosition = null;
                }
                else {
                    buffer += c;
                }
            }
            else {
                // Separator not found
                buffer += c;
                // Last character escapes so ignore next one
                if (previousChar !== "\\") {
                    if (lock === c) {
                        // Lock pair found, unlock
                        lock = null;
                        lastQuotePairPosition = index;
                    }
                    else if (lock === null && (c === "'" || c === '"')) {
                        // Lock character found, but if it was escaped, ignored it
                        lock = c;
                    }
                }
                // If content is not locked and character is either opening or closing bracket, we increase/decrease the level
                if (lock === null && c === "(") {
                    openedRoundBrackets += 1;
                }
                else if (lock === null && c === ")") {
                    if (openedRoundBrackets > 0) {
                        openedRoundBrackets -= 1;
                    }
                }
                else if (lock === null && c === "[") {
                    openedSquareBrackets += 1;
                }
                else if (lock === null && c === "]") {
                    if (openedSquareBrackets > 0) {
                        openedSquareBrackets -= 1;
                    }
                }
                else if (lock === null && c === "{") {
                    openedCurlyBrackets += 1;
                }
                else if (lock === null && c === "}") {
                    if (openedCurlyBrackets > 0) {
                        openedCurlyBrackets -= 1;
                    }
                }
            }
            previousChar = c;
        }
        // If there is some word, append it
        if (buffer.length > 0) {
            // Note that if the lock was opened but not closed, there is potentially last piece that wasn't split and should be
            // This is an edge case so we are treating it as such.
            // Could be optimized further just by saving whether separator was found inside lock but no need
            if (lock !== null) {
                // Gracefully handle edge case situation where following: "test \"test test\"\" test" can happen
                // Another quote is opened after some content with separator inside was ignored, meaning that separation of last buffer will actually result
                // in incorrectly separating content within quotes. So we split only the part after the last quote and prepend the prefix
                if (lastQuotePairPosition) {
                    const substring = str.substr(lastQuotePairPosition, str.length - lastQuotePairPosition);
                    const subcomponents = substring.split(separator);
                    // Prepend the string with the unsplit part
                    if (subcomponents.length > 0) {
                        let firstItem = subcomponents[0];
                        firstItem = `${buffer.substring(0, buffer.length - substring.length)}${firstItem}`;
                        subcomponents.splice(0, 1, firstItem);
                    }
                    // Append separated components
                    components = components.concat(subcomponents);
                }
                else {
                    components = components.concat(buffer.split(separator));
                }
            }
            else {
                components.push(buffer);
            }
        }
        return components;
    }
    /** Tests whether all characters of the string are lowercased and are only letters a-z */
    static isLowercaseLetters(str) {
        return /^[a-z]+$/.test(str);
    }
    /** Tests whether all characters of the string are lowercased and are only letters a-z, including separator dot */
    static isLowercaseLettersCanIncludeDot(str) {
        return /^[a-z.]+$/.test(str);
    }
    /** Tests whether all characters of the string are letters */
    static isLetters(str) {
        return /^[a-zA-Z]+$/.test(str);
    }
    /** Tests whether all characters of the string are letters, including separator dot */
    static isLettersCanIncludeDot(str) {
        return /^[a-zA-Z.]+$/.test(str);
    }
    /** Tests whether the first character of the string is lowercase */
    static isFirstCharacterLetterLowercase(str) {
        if (str.length === 0) {
            return false;
        }
        return !!str[0] && str[0].toLowerCase() === str[0];
    }
    /** Tests whether all characters of the string are spaces */
    static isOnlySpaces(str) {
        return /^[ ]+$/.test(str);
    }
    /** Tests whether all characters of the string are spaces or newlines */
    static isOnlySpacesOrLineBreaks(str) {
        return /^\s*$/.test(str);
    }
    /** Tests whether string represents array definition - starts with [, ends with ]
     * and doesn't contain : unless in double quotes
     */
    static isStructuralArrayDefinition(str) {
        const trimmed = str.trim();
        if (trimmed.startsWith("[") &&
            trimmed.endsWith("]") &&
            str.indexOf(":") === -1) {
            return true;
        }
        return false;
    }
    /** Parses array if it is structuraly viable and retrieves array components */
    static parsedArrayFromDefinition(str) {
        let workingString = str;
        if (!this.isStructuralArrayDefinition(str)) {
            return null;
        }
        // Clear the array definition
        // Remove first and last symbol ([])
        workingString = workingString.trim().substr(1, workingString.length - 2);
        // Split array with ","
        const components = this.splitIgnoringQuotedBracketedContent(workingString, ",");
        return components;
    }
    /** Tests whether string represents dictionary definition - starts with [, ends with ]
     * and contains at least once :
     */
    static isStructuralDictionaryDefinition(str) {
        // TODO: Test this properly, only find definitions that don't have : inside quotes
        const trimmed = str.trim();
        if (trimmed.startsWith("[") &&
            trimmed.endsWith("]") &&
            str.indexOf(":") !== -1) {
            return true;
        }
        return false;
    }
    /** Parses array if it is structuraly viable and retrieves array components */
    static parsedDictionaryFromDefinition(str) {
        let workingString = str;
        if (!this.isStructuralDictionaryDefinition(str)) {
            return null;
        }
        // Clear the array
        workingString = workingString.trim();
        // Remove first and last symbol ([])
        workingString.substr(1, workingString.length - 2);
        // Test whether the remaining string isn't just :. If so, it means we are creating empty dictionary
        if (workingString === ":") {
            return new Map();
        }
        // Split dictionary key-value pairs with ","
        const components = this.splitIgnoringQuotedBracketedContent(workingString, ",");
        // Split keys and values. Format MUST be [key] : [value], otherwise we fail
        const map = new Map();
        for (const component of components) {
            const pair = this.splitIgnoringQuotedContent(component, ":");
            if (pair.length === 2 && pair[0] && pair[1]) {
                // Add the data
                map.set(pair[0], pair[1]);
            }
            else {
                // Format is wrong
                return null;
            }
        }
        return map;
    }
    static replaceAll(str, find, replace) {
        return str.replace(new RegExp(this.escapeRegExp(find), "g"), replace);
    }
    static escapeRegExp(string) {
        return string.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&");
    }
}
